import { PSDJSON, LayerJSON, PostermakerTemplateJSON, RGB, BackgroundItem, ShapeItem, TextItem, TemplateItem, TextLayerAttribute, TextCase } from './types';
import { clipBoxText } from './clipBoxText';

// Helper: Convert RGB string to RGB object
function parseRgbString(color?: string): { r: number; g: number; b: number } | null {
  if (!color) return null;
  const regex = /rgb\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*\)/i;
  const m = regex.exec(color);
  if (!m) return null;
  return {
    r: Number.parseInt(m[1], 10) / 255,
    g: Number.parseInt(m[2], 10) / 255,
    b: Number.parseInt(m[3], 10) / 255
  };
}

// Helper: Convert RGB (0-1 range) to RGB (0-255 range)
function rgbTo255(color?: { r: number; g: number; b: number }): RGB {
  if (!color) return { Red: 255, Green: 255, Blue: 255 };
  return {
    Red: Math.round(color.r * 255),
    Green: Math.round(color.g * 255),
    Blue: Math.round(color.b * 255)
  };
}

// Helper: Normalize text for Fabric.js (preserve newlines, collapse extra to one)
function normalizeTextForFabric(text: string): string {
  return text
    .replaceAll('\r\n', '\n')
    .replaceAll(/\n+/g, '\n')
    .replaceAll(/[^\S\n]+/g, ' ')
    .trim();
}

// Helper: Calculate LS_center, LS_frame, LS_side from bounding box
function getCenterAndFrame(
  box: { x: number; y: number; width: number; height: number },
  templateWidth: number,
  templateHeight: number
) {
  const centerX = (box.x + box.width / 2) / templateWidth;
  const centerY = (box.y + box.height / 2) / templateHeight;
  const originX = box.x / templateWidth;
  const originY = box.y / templateHeight;
  const sizeWidth = box.width / templateWidth;
  const sizeHeight = box.height / templateHeight;
  const LS_side = (box.width / templateWidth).toFixed(2);

  return {
    LS_center: `{${centerX},${centerY}}`,
    LS_frame: `{{${originX}, ${originY}}, {${sizeWidth}, ${sizeHeight}}}`,
    LS_side,
  };
}

// Helper: Get bounding box for layer (adjusts for text point layers)
function getLayerBox(layer: LayerJSON): { x: number; y: number; width: number; height: number } {
  const box = layer.boundingBox;
  if (layer.type !== 'text') return box;
  const textData = layer.rawLayerData?.text;
  if (textData?.shapeType !== 'point') return box;
  const transform = textData?.transform;
  const bounds = textData?.bounds;
  const hasValidTransform = transform && transform.length >= 6;
  const hasValidBounds = bounds?.left?.value != null && bounds?.top?.value != null;
  if (!hasValidTransform || !hasValidBounds) return box;
  const [a, b, c, d] = transform;
  const baselineX = transform[4];
  const baselineY = transform[5];
  const scaleX = Math.hypot(a, b);
  const scaleY = Math.hypot(c, d);
  const boundsLeft = bounds!.left!.value;
  const boundsTop = bounds!.top!.value;
  return {
    x: baselineX + boundsLeft * scaleX,
    y: baselineY + boundsTop * scaleY,
    width: box.width,
    height: box.height
  };
}

// Helper: Map text align string to number
const TextAlignMap: { [key: string]: number } = {
  LEFT: 0,
  left: 0,
  CENTER: 1,
  center: 1,
  RIGHT: 2,
  right: 2
};

/**
 * Extract rotation angle in radians from PSD transform matrix.
 * 
 * PSD provides a 2D transform matrix in format: [a, b, c, d, tx, ty]
 * Rotation is extracted using: rotation = Math.atan2(b, a)
 * 
 * Returns real radians (not scaled, not converted to degrees):
 * - 180° = 3.141592653589793
 * - 90° = 1.5707963267948966
 * - -90° = -1.5707963267948966
 * 
 * @param transform - Transform matrix [a, b, c, d, tx, ty] or undefined
 * @returns Rotation angle in radians as string, or '0' if transform is invalid/missing
 */
function extractRotation(transform?: number[]): string {
  if (!transform || transform.length < 2) {
    return '0';
  }
  
  const [a, b] = transform;
  const angle = Math.atan2(b, a);
  return String(angle);
}

// Helper: PSD multiline text detection
function isPsdMultiline(layer: any, fontSizePx: number) {
  // Case 1: Explicit newline
  if (typeof layer.text === 'string' && layer.text.includes('\n')) {
    return true;
  }

  // Case 2: Visual multiline
  if (
    Array.isArray(layer.boxBounds) &&
    layer.boxBounds.length === 4 &&
    typeof fontSizePx === 'number' &&
    fontSizePx > 0
  ) {
    const boxHeightPx = layer.boxBounds[3];
    if (boxHeightPx > fontSizePx * 1.4) {
      return true;
    }
  }

  return false;
}

// Helper: Calculate opacity from raw opacity value
function calculateOpacityFromRaw(rawOpacity: number): number {
  return rawOpacity === 1 ? 1 : rawOpacity / 255;
}

// Helper: Calculate parent chain opacity
function calculateParentOpacity(parentChain: LayerJSON[]): number {
  let parentOpacity = 1;
  
  for (const parent of parentChain) {
    if (typeof parent.rawOpacity === 'number') {
      parentOpacity *= calculateOpacityFromRaw(parent.rawOpacity);
    } else if (typeof parent.opacity === 'number' && parent.opacity <= 1) {
      parentOpacity *= parent.opacity;
    }
  }
  
  return parentOpacity;
}

/**
 * Calculate final opacity for Postermaker
 */
function getFinalOpacity(layer: LayerJSON, parentChain: LayerJSON[] = []): number {
  let opacity = 1;

  if (typeof layer.rawOpacity === 'number') {
    opacity *= calculateOpacityFromRaw(layer.rawOpacity);
  } else if (typeof layer.opacity === 'number' && layer.opacity <= 1) {
    opacity *= layer.opacity;
  }

  if (typeof layer.fillColorAlpha === 'number') {
    opacity *= layer.fillColorAlpha;
  }

  opacity *= calculateParentOpacity(parentChain);

  return Math.max(0, Math.min(1, opacity));
}

// Helper: Convert final opacity to Postermaker string format
function normalizeOpacity(layer: LayerJSON, parentChain: LayerJSON[] = []): string {
  const finalOpacity = getFinalOpacity(layer, parentChain);
  return String(finalOpacity);
}

const FONT_SUFFIXES = [
  "ExtraBold", "UltraBold", "SemiBold", "DemiBold", "ExtraLight", "UltraLight",
  "Thin", "Black", "Heavy", "Medium", "Light", "Bold", "Italic", "Regular",
];

function trimTrailingSeparators(s: string): string {
  let t = s;
  while (t.length > 0 && (t.endsWith('-') || t.endsWith('_'))) t = t.slice(0, -1);
  return t;
}

function parseStyleSuffixToWeight(suffix: string): number {
  const lower = suffix.toLowerCase();
  if (lower.includes("thin")) return 100;
  if (lower.includes("extralight") || lower.includes("ultralight")) return 200;
  if (lower.includes("light")) return 300;
  if (lower.includes("medium")) return 500;
  if (lower.includes("semibold") || lower.includes("demibold")) return 600;
  if (lower.includes("extrabold") || lower.includes("ultrabold")) return 800;
  if (lower.includes("bold")) return 700;
  if (lower.includes("black") || lower.includes("heavy")) return 900;
  return 400;
}

function extractFamilyAndSuffix(postScriptName: string): { family: string; styleSuffix: string } {
  let family = postScriptName;
  let styleSuffix = "Regular";
  for (const suffix of FONT_SUFFIXES) {
    if (postScriptName.endsWith(suffix)) {
      family = trimTrailingSeparators(postScriptName.slice(0, -suffix.length));
      styleSuffix = suffix;
      break;
    }
  }
  return { family: family || postScriptName, styleSuffix };
}

// Helper: Extract and parse font properties from PSD style.font
function extractFontProperties(layer: LayerJSON): {
  family: string;
  fullName: string;
  weight: number;
  style: string;
} {
  const postScriptName: string = (layer.style as any)?.font?.name || "";
  if (!postScriptName) {
    return { family: "Arial", fullName: "Arial", weight: 400, style: "normal" };
  }

  const { family, styleSuffix } = extractFamilyAndSuffix(postScriptName);
  const lower = styleSuffix.toLowerCase();

  return {
    family,
    fullName: `${family}-${styleSuffix}`,
    weight: parseStyleSuffixToWeight(lower),
    style: lower.includes("italic") ? "italic" : "normal",
  };
}

// Convert PSD JSON to Postermaker Template JSON
export async function convertPSDToPostermakerTemplate(
  psdJson: PSDJSON,
  thumbUrl: string = ''
): Promise<PostermakerTemplateJSON> {
  const templateWidth = psdJson.canvas.width;
  const templateHeight = psdJson.canvas.height;
  const layoutSize = `{${templateWidth},${templateHeight}}`;
  const json_data: TemplateItem[][] = [[]];
  let hasMissingFonts = false;

  // Process root node (background detection)
  const processRootNode = (layers: LayerJSON[]): void => {
    for (const layer of layers) {
      if (!layer.visible) continue;
      const box = layer.boundingBox;
      const coverageX = box.width / templateWidth;
      const coverageY = box.height / templateHeight;
      const isBackground = coverageX >= 0.99 && coverageY >= 0.99;

      if (isBackground) {
        const { LS_center, LS_frame, LS_side } = getCenterAndFrame(
          box,
          templateWidth,
          templateHeight
        );

        const bgItem: BackgroundItem = {
          type: 'background',
          pm_center: LS_center,
          pm_frame: LS_frame,
          pm_side: LS_side,
          pm_rotation: extractRotation(layer.transform),
          pm_opacity: normalizeOpacity(layer, []),
          pm_logo: null,
          pm_backgroundColor: { Red: 255, Green: 255, Blue: 255 }
        };

        if (layer.imagePath) {
          bgItem.pm_layerUrl = layer.imagePath;
        } else {
          const fillColor = parseRgbString(layer.fillColor);
          bgItem.pm_backgroundColor = rgbTo255(fillColor || undefined);
          bgItem.isBaseBackground = true;
        }

        const sw = layer.strokeWeight ?? 0;
        bgItem.pm_strokeWeight = sw <= 1 ? 0 : sw;

        json_data[0].push(bgItem);
        break;
      }
    }
  };

  // Helper: Process background layer
  const processBackgroundLayer = (
    layer: LayerJSON,
    LS_center: string,
    LS_frame: string,
    LS_side: string,
    parentChain: LayerJSON[]
  ): void => {
    const hasBaseBackground = json_data[0].some(
      (item) => item.type === 'background' && 'isBaseBackground' in item && item.isBaseBackground
    );

    const bgItem: BackgroundItem = {
      type: 'background',
      pm_center: LS_center,
      pm_frame: LS_frame,
      pm_side: LS_side,
      pm_rotation: extractRotation(layer.transform),
      pm_opacity: normalizeOpacity(layer, parentChain),
      pm_logo: null,
      pm_backgroundColor: { Red: 255, Green: 255, Blue: 255 }
    };

    if (layer.imagePath) {
      if (!hasBaseBackground) {
        json_data[0] = json_data[0].filter((item) => item.type !== 'background');
      }
      if (hasBaseBackground) {
        bgItem.type = 'sticker';
      }
      bgItem.pm_layerUrl = layer.imagePath;
    } else {
      if (!hasBaseBackground) {
        json_data[0] = json_data[0].filter((item) => item.type !== 'background');
      }
      const fillColor = parseRgbString(layer.fillColor);
      bgItem.pm_backgroundColor = rgbTo255(fillColor || undefined);
      bgItem.isBaseBackground = true;
    }

    bgItem.pm_strokeWeight = (layer.strokeWeight ?? 0) <= 1 ? 0 : (layer.strokeWeight ?? 0);
    json_data[0].push(bgItem);
  };

  // Helper: Calculate font size in pixels for text layer
  const calculateFontSizePx = (layer: LayerJSON): number => {
    const styleFontSize = layer.style?.fontSize;

    if (typeof styleFontSize !== 'number' || styleFontSize <= 0) {
      return 16; // fallback
    }

    const transform = layer.transform;

    if (!transform || transform.length < 4) {
      return styleFontSize; // no scaling info
    }
    
    const c = transform[2];
    const d = transform[3];

    // Extract vertical scale properly (rotation safe)
    const scaleY = Math.hypot(c, d);

    return styleFontSize * scaleY;
  };

  // Helper: Calculate font size in pixels for styleRun (reuses same logic as calculateFontSizePx)
  const calculateRunFontSizePx = (runFontSize: number, layer: LayerJSON): number => {
    if (typeof runFontSize !== 'number' || runFontSize <= 0) {
      return 0;
    }

    const transform = layer.transform;

    if (!transform || transform.length < 4) {
      return runFontSize; // no scaling info
    }
    
    const c = transform[2];
    const d = transform[3];

    // Extract vertical scale properly (rotation safe) - same as calculateFontSizePx
    const scaleY = Math.hypot(c, d);

    return runFontSize * scaleY;
  };

  // Helper: Calculate pm_textLineSpacing for PSD text layers
  const calculateTextLineSpacing = (layer: LayerJSON, templateWidth: number): number | undefined => {
    // Apply ONLY if: layer.type === "text" AND Source is PSD
    if (layer.type !== 'text') {
      return undefined;
    }

    // Get textData from rawLayerData.text
    const textData = layer.rawLayerData?.text;
    if (!textData) {
      return undefined;
    }

    // Get scaleY from textData.transform[3] (default 1)
    const scaleY = textData.transform?.[3] ?? 1;

    // Get fontSize from textData.style?.fontSize
    const fontSize = textData.style?.fontSize ?? 0;
    if (fontSize <= 0) {
      return undefined;
    }

    // Get autoLeading flag
    const autoLeading = textData.style?.autoLeading;

    // Determine leading
    let leadingValue: number;

    if (autoLeading === false) {
      // Manual leading: use textData.style.leading
      const styleLeading = textData.style?.leading;
      if (typeof styleLeading !== 'number' || styleLeading <= 0) {
        return undefined;
      }
      leadingValue = styleLeading;
    } else if (autoLeading === true) {
      // Auto leading: fontSize * multiplier
      const multiplier = textData.paragraphStyle?.autoLeading ?? 1.2;
      leadingValue = fontSize * multiplier;
    } else {
      // autoLeading is undefined - skip calculation
      return undefined;
    }

    // Convert to rendered px: leadingValue * scaleY
    const renderedLineSpacingPx = leadingValue * scaleY;

    // Normalize: renderedLineSpacingPx / templateWidth
    const pm_textLineSpacing = renderedLineSpacingPx / templateWidth;

    return pm_textLineSpacing;
  };

  // Helper: Extract and normalize styleRuns from textData
  const extractStyleRuns = (textData: any): { styleRuns: any[]; isEngineDataFormat: boolean } | null => {
    let rawStyleRuns: any;
    let isEngineDataFormat = false;

    // Case 1: engineData exists and has StyleRun
    if (textData.engineData) {
      let engineData: any;
      try {
        if (typeof textData.engineData === 'string') {
          engineData = JSON.parse(textData.engineData);
        } else {
          engineData = textData.engineData;
        }
        
        if (engineData?.Editor?.Text?.StyleRun) {
          rawStyleRuns = engineData.Editor.Text.StyleRun;
          isEngineDataFormat = true;
        }
      } catch (error) {
        console.debug('Could not parse engineData for styleRuns:', error);
        // Continue to check direct styleRuns
      }
    }

    // Case 2: fallback to direct styleRuns
    if (!rawStyleRuns && Array.isArray(textData.styleRuns)) {
      rawStyleRuns = textData.styleRuns;
      isEngineDataFormat = false;
    }

    if (!rawStyleRuns) {
      return null;
    }

    // Normalize to array (handle single object case)
    let styleRunsArray: any[] = [];
    if (Array.isArray(rawStyleRuns)) {
      styleRunsArray = rawStyleRuns;
    } else if (typeof rawStyleRuns === 'object') {
      styleRunsArray = [rawStyleRuns];
    }

    if (styleRunsArray.length === 0) {
      return null;
    }

    return { styleRuns: styleRunsArray, isEngineDataFormat };
  };

  const fontCapsToTextCase = (fontCaps: number): TextCase => {
    if (fontCaps === 1) return 'SMALL_CAPS';
    if (fontCaps === 2) return 'UPPER';
    if (fontCaps === 3) return 'LOWER';
    return 'ORIGINAL';
  };

  const getTextCaseFromLayer = (layer: LayerJSON): TextCase => {
    const textData = layer.rawLayerData?.text as { style?: { fontCaps?: number; FontCaps?: number }; engineData?: string | object; styleRuns?: Array<{ style?: { fontCaps?: number; FontCaps?: number }; StyleSheet?: { StyleSheetData?: { fontCaps?: number; FontCaps?: number } }; StyleSheetData?: { fontCaps?: number; FontCaps?: number } }> } | undefined;
    if (!textData) return 'ORIGINAL';

    const rootCaps = textData.style?.fontCaps ?? textData.style?.FontCaps ?? 0;
    const styleRunsResult = extractStyleRuns(textData);
    if (!styleRunsResult) return fontCapsToTextCase(rootCaps);

    const { styleRuns: styleRunsArray, isEngineDataFormat } = styleRunsResult;
    const capsFromRuns: (number | undefined)[] = [];

    for (const run of styleRunsArray) {
      const styleSheetData = isEngineDataFormat
        ? (run as { StyleSheet?: { StyleSheetData?: { fontCaps?: number; FontCaps?: number } }; StyleSheetData?: { fontCaps?: number; FontCaps?: number } }).StyleSheet?.StyleSheetData ?? (run as { StyleSheetData?: { fontCaps?: number; FontCaps?: number } }).StyleSheetData
        : (run as { style?: { fontCaps?: number; FontCaps?: number } }).style;
      const caps = styleSheetData?.FontCaps ?? styleSheetData?.fontCaps;
      capsFromRuns.push(caps);
    }

    const definedCaps = capsFromRuns.filter((c): c is number => typeof c === 'number');
    if (definedCaps.length === 0) return fontCapsToTextCase(rootCaps);
    if (definedCaps.length !== capsFromRuns.length) return fontCapsToTextCase(rootCaps);
    const first = definedCaps[0];
    const allSame = definedCaps.every((c) => c === first);
    return allSame ? fontCapsToTextCase(first) : 'ORIGINAL';
  };

  // Helper: Normalize fillColor to {r, g, b} format (0-1 range)
  const normalizeFillColor = (fillColor: any): { r: number; g: number; b: number } | null => {
    if (!fillColor) return null;

    if (fillColor.r !== undefined && fillColor.g !== undefined && fillColor.b !== undefined) {
      // Already in {r, g, b} format - check if 0-1 or 0-255 range
      if (fillColor.r <= 1 && fillColor.g <= 1 && fillColor.b <= 1) {
        return { r: fillColor.r, g: fillColor.g, b: fillColor.b };
      }
      // Assume 0-255 range, convert to 0-1
      return {
        r: fillColor.r / 255,
        g: fillColor.g / 255,
        b: fillColor.b / 255
      };
    }

    if (Array.isArray(fillColor) && fillColor.length >= 3) {
      // Array format - check if 0-1 or 0-255 range
      if (fillColor[0] <= 1 && fillColor[1] <= 1 && fillColor[2] <= 1) {
        return { r: fillColor[0], g: fillColor[1], b: fillColor[2] };
      }
      // Assume 0-255 range, convert to 0-1
      return {
        r: fillColor[0] / 255,
        g: fillColor[1] / 255,
        b: fillColor[2] / 255
      };
    }

    return null;
  };

  // Helper: Extract font properties from styleRun font
  const extractRunFontProperties = (
    font: any,
    baseStyles: { pm_fontWeight: number; pm_fontStyle?: string; pm_fontName: string; pm_fontFamily: string }
  ): { weight: number; style: string; name: string; family: string } => {
    let runFontWeight = baseStyles.pm_fontWeight;
    let runFontStyle = baseStyles.pm_fontStyle || 'normal';
    let runFontName = baseStyles.pm_fontName;
    let runFontFamily = baseStyles.pm_fontFamily;

    const fontName = font?.name || '';
    if (!fontName) {
      return { weight: runFontWeight, style: runFontStyle, name: runFontName, family: runFontFamily };
    }

    runFontName = fontName;
    
    // Extract family from font name
    if (fontName.includes('-')) {
      runFontFamily = fontName.split('-')[0];
      const stylePart = fontName.split('-')[1];
      runFontStyle = /italic/i.test(stylePart) ? 'italic' : 'normal';
      
      // Map weight from style string
      const styleLower = stylePart.toLowerCase();
      if (styleLower.includes('thin')) {
        runFontWeight = 100;
      } else if (styleLower.includes('extralight') || styleLower.includes('ultralight')) {
        runFontWeight = 200;
      } else if (styleLower.includes('light')) {
        runFontWeight = 300;
      } else if (styleLower.includes('medium')) {
        runFontWeight = 500;
      } else if (styleLower.includes('semibold') || styleLower.includes('demibold')) {
        runFontWeight = 600;
      } else if (styleLower.includes('bold')) {
        runFontWeight = 700;
      } else if (styleLower.includes('extrabold') || styleLower.includes('ultrabold')) {
        runFontWeight = 800;
      } else if (styleLower.includes('black') || styleLower.includes('heavy')) {
        runFontWeight = 900;
      }
    }

    return { weight: runFontWeight, style: runFontStyle, name: runFontName, family: runFontFamily };
  };

  // Helper: Build run styles object with only differences from base
  const buildRunStyles = (
    runPmFontSize: number,
    runColor: RGB | undefined,
    runFontWeight: number,
    runFontStyle: string,
    runFontName: string,
    runFontFamily: string,
    baseStyles: {
      pm_fontSize: number;
      pm_textColor: RGB;
      pm_fontWeight: number;
      pm_fontStyle?: string;
      pm_fontName: string;
      pm_fontFamily: string;
    }
  ): TextLayerAttribute['styles'] => {
    const runStyles: TextLayerAttribute['styles'] = {};

    if (Math.abs(runPmFontSize - baseStyles.pm_fontSize) > 0.0001) {
      runStyles.pm_fontSize = runPmFontSize;
    }

    if (runColor && (
      runColor.Red !== baseStyles.pm_textColor.Red ||
      runColor.Green !== baseStyles.pm_textColor.Green ||
      runColor.Blue !== baseStyles.pm_textColor.Blue
    )) {
      runStyles.pm_textColor = runColor;
    }

    if (runFontWeight !== baseStyles.pm_fontWeight) {
      runStyles.pm_fontWeight = runFontWeight;
    }

    if (runFontStyle !== baseStyles.pm_fontStyle) {
      runStyles.pm_fontStyle = runFontStyle;
    }

    if (runFontName !== baseStyles.pm_fontName) {
      runStyles.pm_fontName = runFontName;
    }

    if (runFontFamily !== baseStyles.pm_fontFamily) {
      runStyles.pm_fontFamily = runFontFamily;
    }

    return runStyles;
  };

  // Helper: Extract run data from styleRun
  const extractRunData = (
    styleRun: any,
    isEngineDataFormat: boolean
  ): { runLength: number; styleSheetData: any } | null => {
    let runLength: number;
    let styleSheetData: any;

    if (isEngineDataFormat) {
      // From engineData StyleRun format
      runLength = styleRun.length ?? styleRun.Length ?? styleRun.RunLength;
      styleSheetData = styleRun.StyleSheet?.StyleSheetData ?? styleRun.StyleSheetData;
    } else {
      // From raw styleRuns format
      runLength = styleRun.length;
      styleSheetData = styleRun.style;
    }

    // Defensive checks
    if (typeof runLength !== 'number' || runLength <= 0 || !styleSheetData) {
      return null;
    }

    return { runLength, styleSheetData };
  };

  // Helper: Calculate run styles from normalized style data
  const calculateRunStyles = (
    normalizedStyle: { fontSize?: number; fillColor?: any; font?: any },
    layer: LayerJSON,
    templateWidth: number,
    baseFontSizePx: number,
    baseStyles: {
      pm_fontSize: number;
      pm_textColor: RGB;
      pm_fontWeight: number;
      pm_fontStyle?: string;
      pm_fontName: string;
      pm_fontFamily: string;
    }
  ): TextLayerAttribute['styles'] => {
    // Calculate effective fontSize for this styleRun
    let runFontSizePx = baseFontSizePx;
    if (normalizedStyle.fontSize) {
      const runStyleFontSize = normalizedStyle.fontSize;
      if (typeof runStyleFontSize === 'number' && runStyleFontSize > 0) {
        runFontSizePx = calculateRunFontSizePx(runStyleFontSize, layer);
      }
    }
    const runPmFontSize = runFontSizePx / templateWidth;

    // Extract color from styleRun
    const normalizedColor = normalizeFillColor(normalizedStyle.fillColor);
    const runColor = normalizedColor ? rgbTo255(normalizedColor) : undefined;

    // Extract font properties from styleRun
    const fontProps = normalizedStyle.font
      ? extractRunFontProperties(normalizedStyle.font, baseStyles)
      : {
          weight: baseStyles.pm_fontWeight,
          style: baseStyles.pm_fontStyle || 'normal',
          name: baseStyles.pm_fontName,
          family: baseStyles.pm_fontFamily
        };

    // Build styles object with only differences from base
    return buildRunStyles(
      runPmFontSize,
      runColor,
      fontProps.weight,
      fontProps.style,
      fontProps.name,
      fontProps.family,
      baseStyles
    );
  };

  // Helper: Add or merge attribute into attributes array
  const addOrMergeAttribute = (
    attributes: TextLayerAttribute[],
    runStart: number,
    actualLength: number,
    fullText: string,
    runStyles: TextLayerAttribute['styles']
  ): void => {
    const targetText = fullText.slice(runStart, runStart + actualLength);
    const lastAttr = attributes.at(-1);

    // Check if we can merge with previous attribute
    if (lastAttr &&
        lastAttr.range.start + lastAttr.range.length === runStart &&
        JSON.stringify(lastAttr.styles) === JSON.stringify(runStyles)) {
      // Merge with previous
      lastAttr.range.length += actualLength;
      lastAttr.targetText = fullText.slice(lastAttr.range.start, lastAttr.range.start + lastAttr.range.length);
    } else {
      // Create new attribute
      attributes.push({
        targetText: targetText,
        range: {
          start: runStart,
          length: actualLength
        },
        styles: runStyles
      });
    }
  };

  // Helper: Apply textCase transform to text (does not mutate original)
  const applyTextCaseTransform = (text: string, textCase: TextCase): string => {
    if (textCase === 'UPPER') return text.toUpperCase();
    if (textCase === 'LOWER') return text.toLowerCase();
    return text;
  };

  // Helper: Transform text layer with multi-style support via pm_textLayerAttributes.
  // Uses finalText (same as pm_title) so ranges match rendered text.
  const transformTextLayer = (
    layer: LayerJSON,
    templateWidth: number,
    baseFontSizePx: number,
    baseStyles: {
      pm_fontSize: number;
      pm_textColor: RGB;
      pm_fontWeight: number;
      pm_fontStyle?: string;
      pm_fontName: string;
      pm_fontFamily: string;
    },
    finalText: string
  ): TextLayerAttribute[] | undefined => {
    const textData = layer.rawLayerData?.text;
    if (!textData) return undefined;

    const styleRunsResult = extractStyleRuns(textData);
    if (!styleRunsResult) return undefined;

    const { styleRuns: styleRunsArray, isEngineDataFormat } = styleRunsResult;
    const maxLen = finalText.length;
    if (maxLen === 0) return undefined;

    const attributes: TextLayerAttribute[] = [];
    let currentStart = 0;

    for (const styleRun of styleRunsArray) {
      const runData = extractRunData(styleRun, isEngineDataFormat);
      if (!runData) {
        currentStart += (styleRun.length ?? styleRun.Length ?? styleRun.RunLength ?? 0);
        continue;
      }

      const { runLength, styleSheetData } = runData;
      const runStart = currentStart;
      const runEnd = runStart + runLength;

      // Intersect run with [0, finalText.length] (handles clipped text)
      const clipStart = Math.max(0, runStart);
      const clipEnd = Math.min(runEnd, maxLen);
      const actualLength = Math.max(0, clipEnd - clipStart);

      currentStart += runLength;

      if (actualLength <= 0 || clipStart >= maxLen) continue;

      const normalizedStyle = {
        fontSize: styleSheetData.FontSize ?? styleSheetData.fontSize,
        fillColor: styleSheetData.FillColor ?? styleSheetData.fillColor,
        font: styleSheetData.Font ?? styleSheetData.font
      };

      const runStyles = calculateRunStyles(
        normalizedStyle,
        layer,
        templateWidth,
        baseFontSizePx,
        baseStyles
      );

      if (Object.keys(runStyles).length > 0) {
        addOrMergeAttribute(attributes, clipStart, actualLength, finalText, runStyles);
      }
    }

    return attributes.length > 0 ? attributes : undefined;
  };

  // Helper: Extract scaleX, scaleY from layer transform
  const getScaleFromTransform = (transform?: number[]): { scaleX: number; scaleY: number } => {
    if (!transform || transform.length < 4) {
      return { scaleX: 1, scaleY: 1 };
    }
    const [a, b, c, d] = transform;
    return {
      scaleX: Math.hypot(a, b),
      scaleY: Math.hypot(c, d)
    };
  };

  // Helper: Process text layer
  const processTextLayer = (
    layer: LayerJSON,
    LS_center: string,
    LS_frame: string,
    fontSizePx: number,
    parentChain: LayerJSON[]
  ): void => {
    // 1. rawText
    const rawText = layer.text || '';

    // 2. preserve newlines (only \r\n -> \n)
    const withNewlines = normalizeTextForFabric(rawText);

    // 3. apply textCase transform BEFORE measurement/clipping (do not mutate layer.text)
    const textCase = getTextCaseFromLayer(layer);
    const transformedText = applyTextCaseTransform(withNewlines, textCase);

    // 4. calculate scaled fontSize (fractional, no rounding)
    const { scaleY } = getScaleFromTransform(layer.transform);
    const rawFontSize = layer.style?.fontSize ?? 16;
    const scaledFontSize = rawFontSize * scaleY;

    // 5. detect multiline (using transformed text)
    const isMultiline = isPsdMultiline(layer, scaledFontSize) ||
      transformedText.includes('\n');

    // 6. clip if box text
    const shapeType = layer.rawLayerData?.text?.shapeType;
    const isBoxText = shapeType === 'box';
    const fontProps = extractFontProperties(layer);
    const fontFamily = fontProps.family || fontProps.fullName || 'sans-serif';

    let finalText: string;
    let pm_overflow = false;

    if (isBoxText) {
      const box = layer.boundingBox;
      const boxWidthPx = box.width;
      const boxHeightPx = box.height;
      const pm_textLineSpacingVal = calculateTextLineSpacing(layer, templateWidth);
      const lineSpacingPx = pm_textLineSpacingVal === undefined
        ? scaledFontSize * 1.2
        : pm_textLineSpacingVal * templateWidth;
      try {
        const clipped = clipBoxText({
          text: transformedText,
          fontSizePx: scaledFontSize,
          lineSpacingPx,
          boxWidthPx,
          boxHeightPx,
          fontFamily,
          scaleX: 1,
          scaleY: 1
        });
        finalText = clipped.visibleText;
        pm_overflow = clipped.overflow;
      } catch {
        finalText = transformedText;
      }
    } else {
      finalText = transformedText;
    }

    // 7. build attributes from transformed text (same as pm_title)
    const pm_fontSize = fontSizePx / templateWidth;
    const baseTextColor = layer.color ? rgbTo255(parseRgbString(layer.color) || undefined) : { Red: 0, Green: 0, Blue: 0 };
    const baseStyles = {
      pm_fontSize,
      pm_textColor: baseTextColor,
      pm_fontWeight: fontProps.weight,
      pm_fontStyle: fontProps.style,
      pm_fontName: fontProps.fullName,
      pm_fontFamily: fontProps.family
    };
    const pm_textLayerAttributes = transformTextLayer(
      layer,
      templateWidth,
      fontSizePx,
      baseStyles,
      finalText
    );

    // 8. build final TextItem
    const pm_textLineSpacing = calculateTextLineSpacing(layer, templateWidth);
    const textItem: TextItem = {
      type: 'text',
      pm_center: LS_center,
      pm_frame: LS_frame,
      pm_rotation: extractRotation(layer.transform),
      pm_opacity: normalizeOpacity(layer, parentChain),
      pm_title: finalText,
      pm_fontName: fontProps.fullName,
      pm_fontFamily: fontProps.family,
      pm_fontSize,
      pm_fontWeight: fontProps.weight,
      pm_fontStyle: fontProps.style,
      pm_textAlign: layer.textAlign ? (TextAlignMap[layer.textAlign] ?? 0) : 0,
      pm_textColor: baseTextColor,
      pm_letterSpacing: typeof layer.letterSpacing === 'number' ? ((layer.letterSpacing / 1000) * fontSizePx) / templateWidth : 0,
      pm_textLineSpacing,
      pm_textLayerAttributes,
      pm_fontUrl: null,
      pm_strokeWeight: (layer.strokeWeight ?? 0) <= 1 ? 0 : (layer.strokeWeight ?? 0),
      pm_strokeColor: rgbTo255(parseRgbString(layer.strokeColor) || undefined),
      pm_textCase: textCase,
      pm_isMultilineText: isMultiline,
      pm_overflow: isBoxText ? pm_overflow : undefined
    };

    json_data[0].push(textItem);
  };

  // Helper: Process shape/image layer
  const processShapeLayer = (
    layer: LayerJSON,
    LS_center: string,
    LS_frame: string,
    LS_side: string,
    box: { width: number; height: number },
    parentChain: LayerJSON[]
  ): void => {
    const isImageFill = !!layer.imagePath;
    const shapeItem: ShapeItem = {
      type: isImageFill ? 'sticker' : 'vector',
      pm_center: LS_center,
      pm_frame: LS_frame,
      pm_side: LS_side,
      pm_rotation: extractRotation(layer.transform),
      pm_opacity: normalizeOpacity(layer, parentChain),
      pm_logo: null,
      pm_backgroundColor: isImageFill ? { Red: 255, Green: 255, Blue: 255 } : rgbTo255(parseRgbString(layer.fillColor) || undefined),
      pm_layerUrl: layer.imagePath,
      pm_strokeWeight: (layer.strokeWeight ?? 0) <= 1 ? 0 : (layer.strokeWeight ?? 0),
      pm_strokeColor: rgbTo255(parseRgbString(layer.strokeColor) || undefined),
      pm_dimensions: {
        width: String(box.width),
        height: String(box.height)
      }
    };

    json_data[0].push(shapeItem);
  };

  // Process a single layer
  const processLayer = async (
    layer: LayerJSON,
    parentChain: LayerJSON[] = []
  ): Promise<void> => {
    if (!layer.visible) return;

    const box = getLayerBox(layer);
    const { LS_center, LS_frame, LS_side } = getCenterAndFrame(
      box,
      templateWidth,
      templateHeight
    );

    const isExactBackgroundSize =
      box.width === templateWidth && box.height === templateHeight;

    if (isExactBackgroundSize) {
      processBackgroundLayer(layer, LS_center, LS_frame, LS_side, parentChain);
      return;
    }

    if (layer.type === 'text') {
      const fontSizePx = calculateFontSizePx(layer);
      processTextLayer(layer, LS_center, LS_frame, fontSizePx, parentChain);
      return;
    }

    if (layer.type === 'image' || layer.type === 'shape') {
      processShapeLayer(layer, LS_center, LS_frame, LS_side, box, parentChain);
      return;
    }

    if (layer.type === 'group' && layer.children) {
      const newParentChain = [...parentChain, layer];
      for (const child of layer.children) {
        await processLayer(child, newParentChain);
      }
    }
  };

  // Process root node (background detection)
  processRootNode(psdJson.layers);
  
  // Process all root layers
  for (const layer of psdJson.layers) {
    await processLayer(layer, []);
  }

  return {
    thumbUrl,
    layoutSize,
    json_data,
    hasMissingFonts
  };
}
